/**
 * Inject per-instance selection state sampling into a Three.js material.
 *
 * # The patch (Q18.B)
 *
 * The vertex shader gains:
 *   * a `uniform sampler2D selectionStateTex` — packed per-instance
 *     state bytes (see `selectionStateTexture.ts`),
 *   * a `uniform float selectionStateTexSize` — the square texture
 *     edge length,
 *   * a `varying float vSelectionState` — passed to the fragment
 *     shader,
 * and the fragment shader gains a `mix(baseColor, accentColor, factor)`
 * branch keyed on `vSelectionState`.
 *
 * For LineMaterial the vertex `instanceID` we sample by is derived from
 * the `instanceStart` attribute. Three's BatchedMesh exposes a
 * `getIndirectIndex(gl_DrawID)` chunk; for LineSegments2 (the addon
 * pipeline) we use a per-instance attribute we add manually:
 * `instanceSlotIndex` (a single float). PR-C wires that attribute up
 * via `BatchedLayer.bucket`'s slot map.
 *
 * # Why onBeforeCompile (vs `THREE.ShaderMaterial`)
 *
 * Replacing the entire shader of `LineMaterial` would force us to also
 * re-implement its dashed-line, world-units, and resolution-uniform
 * machinery. `onBeforeCompile` lets us splice into the existing shader
 * code at named hooks, preserving every feature of the upstream
 * material while still adding the selection branch.
 *
 * # Accent color
 *
 * `accentColor` defaults to AutoCAD's default selection cyan
 * (`#00bcd4`). Hover dims the base color toward the accent (factor 0.5);
 * selection replaces it (factor 1.0); preview tints with a yellower
 * accent (`#ffaa00`).
 *
 * # PR-C scope
 *
 * `patchLineMaterial(material, options)` mutates `onBeforeCompile` and
 * sets defaults; the BatchedLayer mesh path adds the per-instance
 * `instanceSlotIndex` attribute on its line geometry. Hatch and text
 * materials gain the same patch in PR-D / PR-E.
 */

import * as THREE from "three";

import type { LineMaterial } from "./thickLines";
import type {
  SelectionStateSampler,
  SelectionStateTexture,
} from "./selectionStateTexture";

export interface StateAwareMaterialOptions {
  selectionStateTexture: SelectionStateTexture;
  selectedColor?: THREE.ColorRepresentation;
  hoveredColor?: THREE.ColorRepresentation;
  previewColor?: THREE.ColorRepresentation;
  /**
   * AutoCAD PREVIEWEFFECT — 0 = dashed-thickened outline (default,
   * cheap), 1 = glowing outline + solid face fill (HW gate, deferred),
   * 2 = both. The shader honours this uniform so the App can flip
   * styles at runtime via `usePreviewPreferences`. Default 0.
   */
  previewEffect?: 0 | 1 | 2;
}

/**
 * Splice the per-instance state sampling into a LineMaterial. The same
 * function works for `THREE.LineBasicMaterial` and `THREE.MeshBasic-
 * Material` so long as the geometry carries an `instanceSlotIndex`
 * attribute (which the BatchedLayer pipeline now sets up).
 *
 * Caller MUST mark `material.needsUpdate = true` after the patch so
 * Three re-compiles. The convenience here is that we attach a stable
 * `onBeforeCompile` hook — re-patching is idempotent.
 */
export function patchMaterialWithSelectionState(
  material: THREE.Material | LineMaterial,
  options: StateAwareMaterialOptions,
): void {
  const selectedColor = new THREE.Color(options.selectedColor ?? 0x00bcd4);
  const hoveredColor = new THREE.Color(options.hoveredColor ?? 0x66e0ff);
  const previewColor = new THREE.Color(options.previewColor ?? 0xffaa00);
  const previewEffect = options.previewEffect ?? 0;
  const tex = options.selectionStateTexture.texture;
  const texSize = options.selectionStateTexture.texSize;
  const compat = options.selectionStateTexture.compat;

  // We attach the uniforms to the material so they survive the
  // onBeforeCompile callback (Three creates a fresh `program.uniforms`
  // map each compile, but `material.userData.selectionUniforms` lets
  // us share state).
  (material as { userData: Record<string, unknown> }).userData ??= {};
  const userData = (material as { userData: Record<string, unknown> })
    .userData;
  userData.selectionUniforms = {
    selectionStateTex: { value: tex },
    selectionStateTexSize: { value: texSize },
    selectedColor: { value: selectedColor },
    hoveredColor: { value: hoveredColor },
    previewColor: { value: previewColor },
    /**
     * 0 = dashed outline (PREVIEWEFFECT=0, AutoCAD default), 1 =
     * glowing outline + face fill (deferred), 2 = both. The shader
     * branches on the integer value at the fragment stage; mutating
     * the uniform is sufficient to switch styles without re-linking.
     */
    previewEffect: { value: previewEffect },
  };

  // fix/selection-highlight-not-applied — subscribe this material to the
  // texture's flush, so a state-byte write forces the sampler's uniform list to
  // be re-uploaded on the next render. Without it three skips the upload
  // whenever the same material is bound two frames running (the single-bucket
  // steady state), and the new byte never reaches the GPU. See
  // `SelectionStateTexture.flush` for the full mechanism.
  options.selectionStateTexture.registerSampler(
    material as unknown as SelectionStateSampler,
  );

  material.onBeforeCompile = (shader) => {
    // Splice uniforms.
    Object.assign(
      shader.uniforms,
      userData.selectionUniforms as Record<string, THREE.IUniform>,
    );

    // Vertex: read state byte → varying. We sample with NEAREST so the
    // single-byte texel comes back un-interpolated.
    const sampleSwizzle = compat === "webgl1" ? ".r" : ".r";
    const vertexHeader = `
attribute float instanceSlotIndex;
uniform sampler2D selectionStateTex;
uniform float selectionStateTexSize;
varying float vSelectionState;
`;
    const vertexBody = `
{
  float idx = instanceSlotIndex;
  float u = mod(idx, selectionStateTexSize) / selectionStateTexSize;
  float v = floor(idx / selectionStateTexSize) / selectionStateTexSize;
  vSelectionState = texture2D(selectionStateTex, vec2(u + 0.5 / selectionStateTexSize, v + 0.5 / selectionStateTexSize))${sampleSwizzle} * 255.0;
}
`;
    shader.vertexShader = shader.vertexShader.replace(
      "void main() {",
      `${vertexHeader}\nvoid main() {\n${vertexBody}`,
    );

    // Fragment: mix base color with accent depending on state byte.
    // 0 = normal (no change), 1 = selected, 2 = hovered, 3 = preview.
    //
    // Hover branch (state=2) implements AutoCAD PREVIEWEFFECT=0 —
    // dashed-thickened outline. Implementation strategy:
    //   1. **Color tint** — stronger mix (0.85) toward `previewColor`
    //      (the PREVIEWCOLOR sysvar). Matches AutoCAD's "highlight as
    //      preview color" behaviour.
    //   2. **Dash pattern** — screen-space modulation using
    //      `gl_FragCoord.x + gl_FragCoord.y` as the dash phase. Dash
    //      length = 8 px on / 4 px off. We re-compute alpha as a
    //      step function and `discard` the dark fragments so the dashes
    //      are visible against any background (LineMaterial writes the
    //      gap as transparent).
    //   3. **Thicken** — true line-thickening requires a parallel
    //      LineSegments2 pass with a wider linewidth (BatchedMesh +
    //      LineMaterial doesn't support per-instance linewidth). The
    //      shader emulates thickening visually by saturating the
    //      hover stroke (full alpha, no AA-fade) so the dashed line
    //      reads as bolder than the surrounding strokes — a credible
    //      proxy until the parallel-pass pipeline lands (tracked under
    //      PR-V9 "hover thickness pass").
    //
    // PREVIEWEFFECT=1 (glow + face fill) — HW-gate deferred. We emit
    // the full PREVIEWEFFECT=0 outline and a console.warn (in JS;
    // the shader simply falls back to outline-only).
    const fragmentHeader = `
uniform vec3 selectedColor;
uniform vec3 hoveredColor;
uniform vec3 previewColor;
uniform float previewEffect;
varying float vSelectionState;

// Screen-space dash pattern. Returns 1.0 inside a dash, 0.0 in a gap.
// Period is 12 px (8 on, 4 off) so a hover'd line shows ≈ 5 dashes per
// 60 px segment — matches AutoCAD's default dash density at typical
// zoom levels.
float previewDash(vec2 fragCoord) {
  float phase = mod(fragCoord.x + fragCoord.y, 12.0);
  return phase < 8.0 ? 1.0 : 0.0;
}
`;
    // Note: this replaces the final `gl_FragColor` write only when
    // present. LineMaterial / LineBasicMaterial writes via
    // `#include <output_fragment>` (Three r150+); we patch that hook.
    const fragmentTail = `
{
  // Round to nearest int — Nearest filtering returns 1.0 = state=1
  // (selected) for a byte value of 1; floats may have ε noise.
  float state = floor(vSelectionState + 0.5);
  if (state > 0.5 && state < 1.5) {
    gl_FragColor.rgb = mix(gl_FragColor.rgb, selectedColor, 1.0);
  } else if (state > 1.5 && state < 2.5) {
    // SELECTIONPREVIEW hover (PREVIEWEFFECT=0 / 2). Apply PREVIEWCOLOR
    // tint via the hoveredColor uniform (the name is historic — it
    // carries the AutoCAD PREVIEWCOLOR value), then dash.
    //
    // PREVIEWEFFECT=1 falls back to outline since face-fill needs a
    // parallel render pass (deferred — tracked PR-V9).
    gl_FragColor.rgb = mix(gl_FragColor.rgb, hoveredColor, 0.85);
    gl_FragColor.a = 1.0;
    if (previewEffect < 0.5 || previewEffect > 1.5) {
      // Effect 0 or 2 → dashed.
      float dashOn = previewDash(gl_FragCoord.xy);
      if (dashOn < 0.5) discard;
    }
  } else if (state > 2.5 && state < 3.5) {
    gl_FragColor.rgb = mix(gl_FragColor.rgb, previewColor, 0.7);
  }
}
`;
    shader.fragmentShader = shader.fragmentShader.replace(
      "void main() {",
      `${fragmentHeader}\nvoid main() {`,
    );
    // Append to the very end of main(). We look for the last closing
    // brace of the entry function — Three's GLSL builder always emits
    // a final `}` after the include chain.
    shader.fragmentShader = shader.fragmentShader.replace(
      /\}\s*$/,
      `${fragmentTail}\n}`,
    );
  };

  // Force a re-link on next frame.
  (material as { needsUpdate: boolean }).needsUpdate = true;
}
